import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { DownloadSimple, Megaphone, ChartLine, Users, CurrencyInr, TrendUp, TrendDown } from "@phosphor-icons/react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar,
} from "recharts";

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "7D" },
  { id: "14d", label: "14D" },
  { id: "30d", label: "30D" },
  { id: "60d", label: "60D" },
  { id: "90d", label: "90D" },
];

function Stat({ icon: Icon, label, value, sub, color = "text-[#1F2A1C]", testid }) {
  return (
    <div className="seva-card" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#56624F] text-xs uppercase tracking-widest"><Icon size={14} weight="fill" /> {label}</div>
      <div className={"font-display font-bold text-2xl mt-1 " + color}>{value}</div>
      {sub ? <div className="text-xs text-[#56624F] mt-0.5">{sub}</div> : null}
    </div>
  );
}

export default function AdminReports() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [preset, setPreset] = useState("30d");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/campaigns").then((r) => {
      const list = r.data || [];
      setCampaigns(list);
      if (list.length && !campaignId) setCampaignId(list[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    api.get(`/admin/reports/campaign/${campaignId}`, { params: { preset } })
      .then((r) => setReport(r.data))
      .finally(() => setLoading(false));
  }, [campaignId, preset]);

  const currentCampaign = campaigns.find((c) => c.id === campaignId);

  const dailyChart = useMemo(() => (report?.daily || []).map((d) => ({
    date: d.date.slice(5),
    revenue: d.revenue,
    donations: d.donations,
  })), [report]);

  const downloadCsv = () => {
    if (!campaignId) return;
    const token = localStorage.getItem("seva_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/campaign/${campaignId}/csv?preset=${preset}`;
    // Use fetch + blob to send auth header, then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `SEVA-${currentCampaign?.slug || campaignId.slice(0, 8)}-${preset}.csv`;
        a.click();
      });
  };

  const t = report?.totals || {};
  const profitColor = (t.profit || 0) >= 0 ? "text-[#2F7A4A]" : "text-[#B84A34]";

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Campaign Reports</h1>
          <p className="text-[#56624F] mt-1">Per-campaign performance — revenue, donors, UTM &amp; Meta Ads ROAS.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm min-w-[220px]" data-testid="reports-campaign-select">
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <div className="flex rounded-xl border border-[#DCE5D4] bg-white overflow-hidden" role="tablist">
            {PRESETS.map((p) => (
              <button key={p.id} onClick={() => setPreset(p.id)} className={"px-3 py-2 text-xs font-semibold transition " + (preset === p.id ? "bg-[#4FAC39] text-white" : "text-[#56624F] hover:bg-[#EEF4E9]")} data-testid={`reports-preset-${p.id}`}>{p.label}</button>
            ))}
          </div>
          <button onClick={downloadCsv} disabled={!campaignId} className="btn-primary inline-flex items-center gap-2 !py-2 !px-4 text-sm disabled:opacity-60" data-testid="reports-download-csv"><DownloadSimple size={16} weight="bold" /> Download CSV</button>
        </div>
      </div>

      {loading && <div className="text-sm text-[#56624F]">Loading report…</div>}

      {!loading && report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={CurrencyInr} label="Revenue" value={formatINR(t.revenue || 0)} sub={`${report.window_days || 0} day window`} testid="stat-revenue" />
            <Stat icon={Users} label="Donations" value={(t.donations || 0).toLocaleString()} sub={`${t.unique_donors || 0} unique donors`} testid="stat-donations" />
            <Stat icon={ChartLine} label="Avg. Gift" value={formatINR(Math.round(t.avg_gift || 0))} testid="stat-avg-gift" />
            <Stat icon={Megaphone} label="Ad Spend" value={formatINR(Math.round(t.ad_spend || 0))} sub={`+${(t.gst_percent || 0).toFixed(0)}% GST = ${formatINR(Math.round(t.total_spend_with_gst || 0))}`} testid="stat-ad-spend" />
            <Stat icon={TrendUp} label="ROAS" value={(t.roas || 0).toFixed(2) + "x"} sub={(t.roas || 0) >= 3 ? "Healthy" : "Optimize campaigns"} color={(t.roas || 0) >= 3 ? "text-[#2F7A4A]" : "text-[#7A5E00]"} testid="stat-roas" />
            <Stat icon={(t.profit || 0) >= 0 ? TrendUp : TrendDown} label="Profit" value={formatINR(Math.round(t.profit || 0))} sub="Revenue − (Ad + GST)" color={profitColor} testid="stat-profit" />
            <Stat icon={CurrencyInr} label="Goal" value={formatINR(report.campaign?.goal_amount || 0)} testid="stat-goal" />
            <Stat icon={ChartLine} label="Progress" value={(report.campaign?.goal_amount ? ((t.revenue / report.campaign.goal_amount) * 100).toFixed(1) : "0.0") + "%"} sub="of goal this window" testid="stat-progress" />
          </div>

          <div className="seva-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Daily Trend</h3>
              <div className="text-xs text-[#56624F]">{report.since} → {report.until}</div>
            </div>
            {dailyChart.length ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyChart} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DCE5D4" />
                    <XAxis dataKey="date" stroke="#56624F" fontSize={11} />
                    <YAxis stroke="#56624F" fontSize={11} />
                    <Tooltip formatter={(v, k) => k === "revenue" ? formatINR(v) : v} />
                    <Line type="monotone" dataKey="revenue" stroke="#4FAC39" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="text-sm text-[#56624F] py-6 text-center">No donations in this window.</div>}
          </div>

          <div className="seva-card !p-0 overflow-hidden">
            <div className="px-5 py-4 border-b border-[#DCE5D4]">
              <h3 className="font-display font-bold text-lg">UTM Sources — ROAS Breakdown</h3>
              <p className="text-xs text-[#56624F]">Matched to Meta Ads campaign by name (when available).</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-utm-table">
                <thead className="bg-[#EEF4E9]">
                  <tr className="text-left text-[#56624F]">
                    <th className="py-3 px-4">Source / Medium</th>
                    <th className="py-3 px-4">UTM Campaign</th>
                    <th className="py-3 px-4 text-right">Donations</th>
                    <th className="py-3 px-4 text-right">Revenue</th>
                    <th className="py-3 px-4 text-right">Ad Spend</th>
                    <th className="py-3 px-4 text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.utm || []).map((u, i) => (
                    <tr key={i} className="border-t border-[#DCE5D4]" data-testid={`reports-utm-row-${i}`}>
                      <td className="py-3 px-4"><div className="font-medium">{u.source}</div><div className="text-xs text-[#56624F]">{u.medium}</div></td>
                      <td className="py-3 px-4 text-xs max-w-xs truncate">{u.campaign}</td>
                      <td className="py-3 px-4 text-right">{u.donations}</td>
                      <td className="py-3 px-4 text-right font-semibold">{formatINR(u.revenue)}</td>
                      <td className="py-3 px-4 text-right">{u.ad_spend ? formatINR(Math.round(u.ad_spend)) : "—"}</td>
                      <td className="py-3 px-4 text-right font-bold">{u.roas ? u.roas.toFixed(2) + "x" : "—"}</td>
                    </tr>
                  ))}
                  {!report.utm?.length && <tr><td colSpan={6} className="py-10 text-center text-[#56624F]">No UTM data.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="seva-card">
            <h3 className="font-display font-bold text-lg mb-3">Donations per day</h3>
            {dailyChart.length ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyChart} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DCE5D4" />
                    <XAxis dataKey="date" stroke="#56624F" fontSize={11} />
                    <YAxis stroke="#56624F" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="donations" fill="#7BC467" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="text-sm text-[#56624F] py-6 text-center">No donations in this window.</div>}
          </div>
        </>
      )}
    </div>
  );
}
